/** 招标文件编写模块类型定义（招标方视角） */

export type TenderPurchaseMethod = 'open' | 'invite' | 'negotiation' | 'consult' | 'single';
export type TenderIndustry = 'engineering' | 'goods' | 'service';
export type TenderEvaluationMethod = 'comprehensive' | 'lowest_price';
export type TenderPriceFormula = 'lowest' | 'average' | 'custom';

export const PURCHASE_METHOD_LABELS: Record<TenderPurchaseMethod, string> = {
  open: '公开招标',
  invite: '邀请招标',
  negotiation: '竞争性谈判',
  consult: '竞争性磋商',
  single: '单一来源采购',
};

export const INDUSTRY_LABELS: Record<TenderIndustry, string> = {
  engineering: '工程建设',
  goods: '货物采购',
  service: '服务采购',
};

export const EVALUATION_METHOD_LABELS: Record<TenderEvaluationMethod, string> = {
  comprehensive: '综合评估法',
  lowest_price: '经评审的最低投标价法',
};

export const PRICE_FORMULA_LABELS: Record<TenderPriceFormula, string> = {
  lowest: '最低价优先',
  average: '基准价法',
  custom: '自定义公式',
};

/** 章节分部类型：决定编制提示词的口径 */
export type TenderPartKind =
  | 'notice'
  | 'instruction'
  | 'evaluation'
  | 'contract'
  | 'spec'
  | 'format'
  | 'default';

export const PART_KIND_LABELS: Record<TenderPartKind, string> = {
  notice: '公告/邀请书',
  instruction: '须知条款',
  evaluation: '评标办法',
  contract: '合同条款',
  spec: '技术/服务需求',
  format: '格式附件',
  default: '其他',
};

/** 模板结构树节点（模板里的原始结构，未实例化） */
export interface TenderStructureNode {
  title: string;
  section?: string;
  kind?: TenderPartKind;
  description?: string;
  word_count_range?: string;
  children?: TenderStructureNode[];
}

export interface TenderTemplate {
  id: number;
  user_id: number | null;
  name: string;
  purchase_method: TenderPurchaseMethod;
  industry: TenderIndustry;
  description: string | null;
  structure: TenderStructureNode[];
  version: string;
  is_builtin: boolean;
  created_at: string | null;
}

/** 立项信息 */
export interface TenderProjectInfo {
  id: number;
  project_id: number;
  template_id: number | null;
  template_name: string | null;
  purchase_method: TenderPurchaseMethod;
  industry: TenderIndustry;
  purchaser: string | null;
  agency: string | null;
  project_no: string | null;
  budget: string | null;
  price_cap: string | null;
  duration: string | null;
  location: string | null;
  packages: Array<{ name?: string; content?: string; budget?: string }>;
  evaluation_method: TenderEvaluationMethod;
  price_formula: TenderPriceFormula;
  price_score: number;
  qualification: Record<string, string[]>;
  deposit: string | null;
  bid_deadline_days: number;
  legal_basis: string | null;
  notes: string | null;
  /** 技术要求（类别 → 要求条目列表），编制技术规格书时逐条落实 */
  technical_requirements: Record<string, string[]>;
  /** 评分要求（评分因素/分值/评分标准），编制评标办法时据此生成评分表 */
  scoring_requirements: Array<{ item?: string; score?: string; standard?: string }>;
  created_at: string | null;
  updated_at: string | null;
}

/** 立项信息表单可提交的字段（部分更新） */
export type TenderInfoPayload = Partial<
  Pick<
    TenderProjectInfo,
    | 'purchaser'
    | 'agency'
    | 'project_no'
    | 'budget'
    | 'price_cap'
    | 'duration'
    | 'location'
    | 'packages'
    | 'purchase_method'
    | 'industry'
    | 'evaluation_method'
    | 'price_formula'
    | 'price_score'
    | 'qualification'
    | 'deposit'
    | 'bid_deadline_days'
    | 'legal_basis'
    | 'notes'
    | 'technical_requirements'
    | 'scoring_requirements'
  >
>;

/** 招标文件项目的目录节点（复用 outline_node） */
export interface TenderOutlineNode {
  id: number;
  project_id: number;
  node_id: string;
  stable_id: string;
  parent_node_id: string | null;
  title: string;
  description: string | null;
  word_count_range: string | null;
  sort_order: number;
  section: string;
  part_kind: TenderPartKind;
}

/** 招标文件项目 */
export interface TenderProject {
  id: number;
  name: string;
  description: string | null;
  status: string;
  current_step: number;
  doc_kind: string;
  created_at: string | null;
  updated_at: string | null;
  /** 列表项标记：该项目是别人共享给我的 */
  shared?: boolean;
}

export interface TenderProjectDetail {
  project: TenderProject;
  info: TenderProjectInfo | null;
  outline: TenderOutlineNode[];
  /** stable_id → 已生成/已保存的正文（Markdown） */
  contents: Record<string, string>;
  /** 是否为项目所有者（共享项目不显示版本/共享管理入口） */
  is_owner: boolean;
}

/** 条款库类别 */
export type TenderClauseCategory =
  | 'qualification'
  | 'performance'
  | 'personnel'
  | 'financial'
  | 'credit'
  | 'evaluation'
  | 'contract'
  | 'spec'
  | 'disqualification'
  | 'other';

export const CLAUSE_CATEGORY_LABELS: Record<TenderClauseCategory, string> = {
  qualification: '资格条件',
  performance: '业绩要求',
  personnel: '人员要求',
  financial: '财务要求',
  credit: '信用要求',
  evaluation: '评标办法',
  contract: '合同条款',
  spec: '技术要求',
  disqualification: '废标条款',
  other: '其他',
};

/** 条款库条目 */
export interface TenderClause {
  id: number;
  user_id: number | null;
  category: TenderClauseCategory;
  title: string;
  content: string;
  purchase_methods: TenderPurchaseMethod[];
  industry: TenderIndustry | null;
  source: string | null;
  legal_basis: string | null;
  tags: string[];
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** 价格类型 */
export type TenderPriceType = 'budget' | 'price_cap' | 'award' | 'market' | 'cost';

export const PRICE_TYPE_LABELS: Record<TenderPriceType, string> = {
  budget: '预算价',
  price_cap: '最高限价',
  award: '中标价',
  market: '市场价',
  cost: '成本价',
};

/** 价格参考条目 */
export interface TenderPriceReference {
  id: number;
  user_id: number | null;
  name: string;
  category: string | null;
  region: string | null;
  unit: string | null;
  price: number;
  price_type: TenderPriceType;
  source: string | null;
  reference_date: string | null;
  note: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** 历史招标文件解析出的模板结构（保存前预览） */
export interface TenderTemplateParseResult {
  name: string;
  structure: TenderStructureNode[];
  source: 'markdown' | 'llm';
  node_count: number;
  text_length?: number;
}

/** 合规检查结论 */
export type TenderComplianceStatus = 'pass' | 'warning' | 'risk';

export const COMPLIANCE_STATUS_LABELS: Record<TenderComplianceStatus, string> = {
  pass: '通过',
  warning: '需修改',
  risk: '存在风险',
};

export const COMPLIANCE_STATUS_CLASSES: Record<TenderComplianceStatus, string> = {
  pass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  risk: 'border-rose-200 bg-rose-50 text-rose-700',
};

/** 检查项严重程度 */
export type TenderComplianceSeverity = 'must' | 'should' | 'reference';

export const COMPLIANCE_SEVERITY_LABELS: Record<TenderComplianceSeverity, string> = {
  must: '必须合规',
  should: '应当',
  reference: '提示',
};

/** 检查项类别 */
export type TenderComplianceCategory =
  | 'completeness'
  | 'time_limit'
  | 'qualification'
  | 'evaluation'
  | 'spec'
  | 'contract'
  | 'price'
  | 'fairness'
  | 'other';

export const COMPLIANCE_CATEGORY_LABELS: Record<TenderComplianceCategory, string> = {
  completeness: '完整性',
  time_limit: '时限',
  qualification: '资格条件',
  evaluation: '评标办法',
  spec: '技术要求',
  contract: '合同条款',
  price: '价格',
  fairness: '公平竞争',
  other: '其他',
};

/** 单项检查结果 */
export interface TenderComplianceItem {
  code: string;
  category: string;
  title: string;
  status: TenderComplianceStatus;
  finding: string;
  suggestion: string;
  legal_basis: string;
  severity: TenderComplianceSeverity;
  location: string;
  /** rule=确定性规则；llm=模型语义核查 */
  source: 'rule' | 'llm';
}

/** 合规检查项（可配置） */
export interface TenderComplianceRule {
  id: number;
  user_id: number | null;
  code: string;
  category: string;
  title: string;
  check_prompt: string;
  legal_basis: string | null;
  severity: TenderComplianceSeverity;
  purchase_methods: TenderPurchaseMethod[];
  industry: TenderIndustry | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** 合规审核报告 */
export interface TenderComplianceReport {
  id: number;
  project_id: number;
  status: 'passed' | 'warning' | 'risk';
  total: number;
  passed_count: number;
  warning_count: number;
  risk_count: number;
  items: TenderComplianceItem[];
  summary_md: string | null;
  rule_count: number;
  llm_rule_count: number;
  model_name: string | null;
  created_at: string | null;
}

export const COMPLIANCE_OVERALL_LABELS: Record<'passed' | 'warning' | 'risk', string> = {
  passed: '通过',
  warning: '需修改',
  risk: '存在风险',
};
